import type {
	NotificationItem,
	NotificationListQuery,
	NotificationLocale,
	NotificationPage,
	NotificationPreferences,
	NotificationPreferencesUpdate,
	NotificationReadBySourceRequest,
	NotificationUnreadCount,
	Order,
} from "../types";
import type {
	NotificationTopic,
	NotificationTopicCounts,
	NotificationTopicSettings,
	NotificationTopicSettingsUpdate,
} from "@osmix/contracts";
import { DEFAULT_NOTIFICATION_PREFERENCES, NotificationTopicSchema, notificationTopicOfKind } from "../types";
import { sleep } from "@/lib/utils";

export type MockReceiptData = {
	orderNumber: string;
	dateKey: string;
	shopName: string;
	lines: Array<{
		nameKey: string;
		detailKey: string;
		quantity: number;
		unitPrice: string;
		lineTotal: string;
	}>;
	subtotal: string;
	delivery: string;
	total: string;
	cardLastFour: string;
	addressKey: string;
};

type Seed = Omit<NotificationItem, "title" | "body" | "readAt"> & {
	content: Record<NotificationLocale, { title: string; body: string }>;
	readAt?: string | null;
};

const localized = (
	ru: { title: string; body: string },
	en: { title: string; body: string },
	hy: { title: string; body: string },
): Seed["content"] => ({ ru, en, hy });

const demoReceipt: MockReceiptData = {
	orderNumber: "10482",
	dateKey: "notifications.receipt.today",
	shopName: "Lumi Studio",
	lines: [
		{
			nameKey: "notifications.receipt.items.mugSet",
			detailKey: "notifications.receipt.items.mugSetDetail",
			quantity: 2,
			unitPrice: "6 500 ₽",
			lineTotal: "13 000 ₽",
		},
		{
			nameKey: "notifications.receipt.items.tableRunner",
			detailKey: "notifications.receipt.items.tableRunnerDetail",
			quantity: 1,
			unitPrice: "9 900 ₽",
			lineTotal: "9 900 ₽",
		},
	],
	subtotal: "22 900 ₽",
	delivery: "2 000 ₽",
	total: "24 900 ₽",
	cardLastFour: "4242",
	addressKey: "notifications.receipt.addressMoscow",
};

const visibleSeeds: Seed[] = [
	{
		id: "receipt-order-10482",
		campaignId: "purchase-order-10482",
		kind: "purchase_receipt",
		category: "financial",
		content: localized(
			{ title: "Заказ №10482 подтверждён", body: "Мы получили оплату 24 900 ₽ за 3 товара. Чек готов." },
			{ title: "Order #10482 is confirmed", body: "We received your payment of 24,900 ₽ for 3 items. Your receipt is ready." },
			{ title: "№10482 պատվերը հաստատված է", body: "Ստացել ենք 24 900 ₽ վճարումը՝ 3 ապրանքի համար։ Կտրոնը պատրաստ է։" },
		),
		data: { sourceId: "10482", orderId: "10482", itemCount: 3, total: 24900, currency: "RUB", mockReceipt: demoReceipt },
		action: { kind: "order", orderId: "10482" },
		readAt: null,
		createdAt: "2026-07-10T10:30:00.000Z",
	},
	{
		id: "follow-up-care-10482",
		campaignId: "follow-up-order-10482",
		kind: "post_purchase_follow_up",
		category: "trade",
		content: localized(
			{ title: "Забота помогает вещам служить дольше", body: "Покупка из Lumi Studio создана надолго. Советы по уходу ждут в заказе." },
			{ title: "A little care goes a long way", body: "Your Lumi Studio purchase was made to last. Find care details with your order." },
			{ title: "Խնամքը երկարացնում է իրերի կյանքը", body: "Lumi Studio-ից ձեր գնումը երկար օգտագործելու համար է։ Խնամքի խորհուրդները պատվերի էջում են։" },
		),
		data: { sourceId: "10482", orderId: "10482" },
		action: { kind: "order", orderId: "10482" },
		readAt: null,
		createdAt: "2026-07-10T08:30:00.000Z",
	},
	{
		id: "admin-delivery-update",
		campaignId: "admin-delivery-update-campaign",
		kind: "admin_message",
		category: "system",
		content: localized(
			{ title: "Доставка стала понятнее", body: "Мы обновили статусы доставки, чтобы важные детали было проще заметить." },
			{ title: "A smoother delivery experience", body: "We refreshed delivery updates so important details are easier to spot." },
			{ title: "Առաքումն ավելի հասկանալի է դարձել", body: "Մենք թարմացրել ենք առաքման կարգավիճակները։" },
		),
		data: {},
		readAt: "2026-07-09T12:00:00.000Z",
		createdAt: "2026-07-09T11:45:00.000Z",
	},
];

// A deterministic scheduled fixture intentionally stays outside visibleSeeds.
// Offline clients have no timers: only a backend/mock worker would materialize it.
export const futureFollowUpFixture = {
	dueAt: "2099-01-01T00:00:00.000Z",
	orderId: "10482",
};

const readState = new Map(visibleSeeds.map((seed) => [seed.id, seed.readAt ?? null]));
// Веб-макет «Профиль»: скрытые из ленты («×» / «Очистить все») и флаги тем.
const archived = new Set<string>();
// Ленивая инициализация: контракты не трогаем на этапе загрузки модуля.
let topicSettingsState: NotificationTopicSettings | null = null;
const currentTopicSettings = (): NotificationTopicSettings =>
	(topicSettingsState ??= Object.fromEntries(
		NotificationTopicSchema.options.map((t) => [t, true]),
	) as NotificationTopicSettings);
const topicOf = (seed: Seed) => notificationTopicOfKind(seed.kind, seed.data);
const visible = (seed: Seed) => {
	if (archived.has(seed.id)) return false;
	const topic = topicOf(seed);
	return !topic || currentTopicSettings()[topic];
};
const parseTopics = (raw?: string): NotificationTopic[] =>
	(raw ?? "").split(",").map((t) => t.trim()).filter((t): t is NotificationTopic => NotificationTopicSchema.safeParse(t).success);

const resolve = (seed: Seed, locale: NotificationLocale): NotificationItem => {
	const { content, ...item } = seed;
	return { ...item, ...content[locale], readAt: readState.get(seed.id) ?? null };
};

export const ensureMockPurchaseReceipt = (order: Order) => {
	if (visibleSeeds.some((seed) => seed.kind === "purchase_receipt" && seed.data.sourceId === order.id)) return;
	visibleSeeds.unshift({
		id: `receipt-${order.id}`,
		campaignId: `purchase-${order.id}`,
		kind: "purchase_receipt",
		category: "financial",
		content: localized(
			{ title: `Заказ №${order.id} подтверждён`, body: `Оплата получена. В заказе ${order.itemCount} товар(ов).` },
			{ title: `Order #${order.id} is confirmed`, body: `Payment received for ${order.itemCount} item(s).` },
			{ title: `№${order.id} պատվերը հաստատված է`, body: `Վճարումը ստացված է՝ ${order.itemCount} ապրանքի համար։` },
		),
		data: { sourceId: order.id, orderId: order.id, itemCount: order.itemCount, total: order.total, currency: "RUB", paidAt: new Date().toISOString() },
		action: { kind: "order", orderId: order.id },
		readAt: null,
		createdAt: new Date().toISOString(),
	});
	readState.set(`receipt-${order.id}`, null);
};

export const mockNotifications = {
	list: async (query: NotificationListQuery): Promise<NotificationPage> => {
		await sleep(80);
		const locale = query.locale ?? "ru";
		const topics = parseTopics(query.topics);
		const filtered = visibleSeeds.filter(
			(seed) =>
				visible(seed) &&
				(topics.length === 0 || topics.includes(topicOf(seed) as NotificationTopic)) &&
				(query.filter !== "unread" || !readState.get(seed.id)) &&
				(query.filter !== "read" || Boolean(readState.get(seed.id))) &&
				// fd-notif-17: фильтр ленты по категории.
				(!query.category || seed.category === query.category),
		);
		const start = query.cursor ? Number.parseInt(query.cursor, 10) : 0;
		const limit = query.limit ?? 20;
		const items = filtered.slice(start, start + limit).map((seed) => resolve(seed, locale));
		const next = start + items.length;
		return { items, nextCursor: next < filtered.length ? String(next) : null, hasMore: next < filtered.length };
	},
	unreadCount: async (): Promise<NotificationUnreadCount> => {
		await sleep(80);
		return { count: visibleSeeds.filter((seed) => visible(seed) && !readState.get(seed.id)).length };
	},
	detail: async (id: string, locale: NotificationLocale): Promise<NotificationItem> => {
		await sleep(80);
		const seed = visibleSeeds.find((item) => item.id === id);
		if (!seed) throw new Error("Notification not found");
		return resolve(seed, locale);
	},
	markRead: async (id: string): Promise<void> => {
		await sleep(60);
		if (!readState.has(id)) throw new Error("Notification not found");
		if (!readState.get(id)) readState.set(id, new Date().toISOString());
	},
	markAllRead: async (): Promise<void> => {
		await sleep(60);
		const now = new Date().toISOString();
		for (const seed of visibleSeeds) if (!readState.get(seed.id)) readState.set(seed.id, now);
	},
	markReadBySource: async (request: NotificationReadBySourceRequest): Promise<void> => {
		await sleep(60);
		const now = new Date().toISOString();
		for (const seed of visibleSeeds) {
			if (seed.kind === request.kind && seed.data.sourceId === request.sourceId) readState.set(seed.id, now);
		}
	},
	archive: async (id: string): Promise<void> => {
		await sleep(60);
		if (!readState.has(id)) throw new Error("Notification not found");
		archived.add(id);
	},
	archiveAll: async (topicsList?: NotificationTopic[]): Promise<void> => {
		await sleep(60);
		for (const seed of visibleSeeds) {
			if (!topicsList?.length || topicsList.includes(topicOf(seed) as NotificationTopic)) archived.add(seed.id);
		}
	},
	getTopicSettings: async (): Promise<NotificationTopicSettings> => {
		await sleep(60);
		return { ...currentTopicSettings() };
	},
	updateTopicSettings: async (patch: NotificationTopicSettingsUpdate): Promise<NotificationTopicSettings> => {
		await sleep(100);
		topicSettingsState = { ...currentTopicSettings(), ...patch };
		return { ...topicSettingsState };
	},
	topicCounts: async (filter: "all" | "unread" | "read" = "all"): Promise<NotificationTopicCounts> => {
		await sleep(60);
		const topics = Object.fromEntries(NotificationTopicSchema.options.map((t) => [t, 0])) as Record<NotificationTopic, number>;
		let total = 0;
		for (const seed of visibleSeeds) {
			if (!visible(seed)) continue;
			if (filter === "unread" && readState.get(seed.id)) continue;
			if (filter === "read" && !readState.get(seed.id)) continue;
			total += 1;
			const topic = topicOf(seed);
			if (topic) topics[topic] += 1;
		}
		return { total, topics };
	},
	// fd-notif-02/14/15/20: настройки уведомлений покупателя (in-memory).
	getPreferences: async (): Promise<NotificationPreferences> => {
		await sleep(80);
		return { ...preferences, categories: { ...preferences.categories }, mutedShopIds: [...preferences.mutedShopIds] };
	},
	updatePreferences: async (patch: NotificationPreferencesUpdate): Promise<NotificationPreferences> => {
		await sleep(120);
		preferences = {
			categories: { ...preferences.categories, ...(patch.categories ?? {}) },
			marketingFrequency: patch.marketingFrequency ?? preferences.marketingFrequency,
			mutedShopIds: patch.mutedShopIds ? [...patch.mutedShopIds] : preferences.mutedShopIds,
			mutedCategoryIds: patch.mutedCategoryIds ? [...patch.mutedCategoryIds] : (preferences.mutedCategoryIds ?? []),
			signals: { ...preferences.signals, ...(patch.signals ?? {}) },
			email: {
				enabled: patch.email?.enabled ?? preferences.email?.enabled ?? true,
				marketing: patch.email?.marketing ?? preferences.email?.marketing ?? true,
			},
		};
		return { ...preferences, categories: { ...preferences.categories }, mutedShopIds: [...preferences.mutedShopIds] };
	},
	// fd-platform-04: в моке бота нет — блок Telegram не показывается.
	telegramStatus: async () => {
		await sleep(40);
		return { enabled: false, botUsername: "", linked: false, username: null, linkedAt: null };
	},
	telegramLink: async () => {
		await sleep(40);
		throw new Error("Telegram не настроен");
	},
	telegramUnlink: async () => {
		await sleep(40);
	},
};

// Снимок настроек уведомлений — по умолчанию из контракта.
let preferences: NotificationPreferences = {
	categories: { ...DEFAULT_NOTIFICATION_PREFERENCES.categories },
	marketingFrequency: DEFAULT_NOTIFICATION_PREFERENCES.marketingFrequency,
	mutedShopIds: [...DEFAULT_NOTIFICATION_PREFERENCES.mutedShopIds],
	signals: { ...DEFAULT_NOTIFICATION_PREFERENCES.signals },
	email: { enabled: true, marketing: true },
	mutedCategoryIds: [],
};
